import React, { useEffect, useState } from "react";
import SwitchButton from "../../../../../Components/SwitchButton/SwitchButton";
import {
  fetchWarehousePermissions,
  assignWarehousePermissions,
} from "../../../../../services/warehouse.services";
import { useTranslation } from "react-i18next";

const Permissions = ({ client, setSnack, onClientUpdated }) => {
  const { t } = useTranslation("warehouse");
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pendingIds, setPendingIds] = useState([]);

  const loadPermissions = async () => {
    setLoading(true);
    const res = await fetchWarehousePermissions();
    if (res?.error) {
      setSnack?.((s) => ({
        ...s,
        open: true,
        text:
          res?.error?.response?.data?.message ?? t("clientSettings.failedToLoadPermissions"),
        severity: "error",
      }));
      setPermissions([]);
    } else {
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];
      setPermissions(list);
      const assigned = Array.isArray(client?.permissions)
        ? client.permissions.map((p) => p?.id).filter((id) => id != null)
        : [];
      setSelectedIds(assigned);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  const togglePermission = async (id) => {
    if (!client?.id || pendingIds.includes(id)) return;

    const wasSelected = selectedIds.includes(id);
    const nextSelected = wasSelected
      ? selectedIds.filter((p) => p !== id)
      : [...selectedIds, id];

    setSelectedIds(nextSelected);
    setPendingIds((prev) => [...prev, id]);

    const res = await assignWarehousePermissions({
      user_warehouse_id: client.id,
      warehouse_permission_ids: nextSelected,
    });

    setPendingIds((prev) => prev.filter((p) => p !== id));

    if (res?.error) {
      setSelectedIds((prev) =>
        wasSelected ? [...prev, id] : prev.filter((p) => p !== id)
      );
      setSnack?.((s) => ({
        ...s,
        open: true,
        text:
          res?.error?.response?.data?.message ?? t("clientSettings.failedToUpdatePermission"),
        severity: "error",
      }));
      return;
    }

    const updated = res?.data?.result;
    if (updated) onClientUpdated?.(updated);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="border border-ui-border rounded-lg p-4 animate-pulse"
          >
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-ui-border rounded" />
              <div className="flex-1">
                <div className="h-3.5 bg-ui-border rounded w-1/3 mb-2" />
                <div className="h-3 bg-ui-surface-sunken rounded w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!permissions.length) {
    return (
      <div className="text-sm text-ui-text-muted">{t("clientSettings.noPermissionsAvailable")}</div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {permissions.map((perm) => {
        const checked = selectedIds.includes(perm.id);
        const isPending = pendingIds.includes(perm.id);
        return (
          <div
            key={perm.id}
            onClick={() => !isPending && togglePermission(perm.id)}
            className={`border border-ui-border rounded-lg p-4 flex items-center justify-between gap-3 transition-colors ${
              isPending
                ? "opacity-60 cursor-wait"
                : "cursor-pointer hover:border-brand"
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ui-text">
                {perm?.description ?? perm?.name ?? `#${perm?.id}`}
              </div>
            </div>
            <SwitchButton
              checked={checked}
              disabled={isPending}
              onClick={(e) => e.stopPropagation()}
              onChange={() => togglePermission(perm.id)}
            />
          </div>
        );
      })}
    </div>
  );
};

export default Permissions;
